  /**
   * AI Model Provider Configuration
   *
   * Centralizes model provider configuration for use across agents and tools.
   * This module provides model configurations for both Mastra agents and direct AI SDK usage.
   *
   * @example
   * ```typescript
   * // For Mastra agents (config objects with url/apiKey)
   * import { mastraModels } from "@/lib/ai/providers";
   * const agent = new Agent({ model: mastraModels.fast, ... });
   * ```
   */

  import { createOpenAICompatible } from "@ai-sdk/openai-compatible";
  import {
    createLitellmHeaders,
    litellmFetch,
    litellmProxyUrl,
    litellmServiceToken,
  } from "@/lib/ai/litellm-auth";
  import { AUTO_MODEL_ID } from "@/lib/ai/router/constants";

  // =============================================================================
  // LITELLM PROXY PROVIDER
  // =============================================================================

  /**
   * LiteLLM proxy provider - routes to multiple LLM providers via unified API.
   * The provider automatically appends /chat/completions to the baseURL,
   * so we only need to provide the base API path.
   *
   * We use @ai-sdk/openai-compatible (NOT @ai-sdk/openai) because LiteLLM
   * surfaces provider reasoning/thinking as a `reasoning_content` field on
   * chat-completions stream deltas, which only the openai-compatible parser
   * turns into reasoning stream parts. Keep the dependency on 2.x: 3.x targets
   * provider spec v4 and is rejected by the ai@6 / Mastra (spec v3) stack.
   */
  const baseURL = litellmProxyUrl;
  const apiKey = litellmServiceToken;

  /**
   * openai-compatible serializes assistant reasoning parts back onto follow-up
   * request messages as `reasoning_content`. Anthropic requires signed thinking
   * blocks when continuing tool loops with thinking on, so forwarding unsigned
   * echoes risks 400s. Strip them to keep outbound requests identical to the
   * previous @ai-sdk/openai behavior (whose parser dropped reasoning entirely).
   */
  function stripEchoedReasoning(args: Record<string, unknown>): Record<string, unknown> {
    if (Array.isArray(args.messages)) {
      args.messages = args.messages.map((message: unknown) => {
        if (message && typeof message === "object" && "reasoning_content" in message) {
          const rest = { ...(message as Record<string, unknown>) };
          delete rest.reasoning_content;
          return rest;
        }
        return message;
      });
    }
    return args;
  }

  function createLitellmProvider(fetchImpl: typeof globalThis.fetch) {
    return createOpenAICompatible({
      name: "litellm",
      baseURL,
      apiKey,
      fetch: fetchImpl,
      // Parity with @ai-sdk/openai, which always sent stream_options.include_usage
      // (litellm-proxy also forces it upstream).
      includeUsage: true,
      transformRequestBody: stripEchoedReasoning,
    });
  }

  const litellm = createLitellmProvider(litellmFetch as typeof globalThis.fetch);

  /**
   * Substitution disclosure set by litellm-proxy (AIR-503): present when the
   * model that actually served differs from the requested one. Duplicated
   * from apps/litellm-proxy/src/utils/served-model.ts — separate deployables,
   * no shared runtime import.
   */
  export const SERVED_MODEL_HEADER = "x-aireserve-served-model";

  /** Callback fired when a proxy response discloses a model substitution. */
  export type OnServedModel = (servedModel: string) => void;

  function reportServedModel(response: Response, onServedModel?: OnServedModel): Response {
    if (onServedModel) {
      const served = response.headers.get(SERVED_MODEL_HEADER);
      if (served) onServedModel(served);
    }
    return response;
  }

  function createScopedLitellmFetch(
    extraHeaders: Record<string, string>,
    onServedModel?: OnServedModel,
  ): typeof globalThis.fetch {
    return (async (input, init) => {
      const headers = await createLitellmHeaders(init?.headers);

      for (const [key, value] of Object.entries(extraHeaders)) {
        headers.set(key, value);
      }

      return reportServedModel(await fetch(input, { ...init, headers }), onServedModel);
    }) as typeof globalThis.fetch;
  }

  function createScopedLitellm(
    extraHeaders: Record<string, string>,
    onServedModel?: OnServedModel,
  ) {
    return createLitellmProvider(createScopedLitellmFetch(extraHeaders, onServedModel));
  }

  // =============================================================================
  // MODEL CONFIGURATION
  // =============================================================================

  /**
   * Model IDs for Mastra agents (string format: "provider/model-name").
   * Use these when configuring Mastra agents.
   *
   * Note: Uses "openai/" prefix because we're using the OpenAI SDK provider,
   * even though LiteLLM routes to actual providers (Google, OpenAI, etc.)
   */
  export const modelIds = {
    /** Default model - Gemini 2.5 Pro (best quality + tool use) */
    default: "openai/gemini-2.5-pro",
    /** Fast, cost-effective model - Gemini 2.5 Flash */
    fast: "openai/gemini-2.5-flash",
  } as const;

  /**
   * Mastra-compatible model configurations
   * These use the config object format that Mastra accepts
   */
  export const mastraModels = {
    default: {
      id: "openai/gemini-2.5-pro" as const,
      url: baseURL,
      apiKey,
    },
    fast: {
      id: "openai/gemini-2.5-flash" as const,
      url: baseURL,
      apiKey,
    },
  } as const;

  /**
   * Available model tiers derived from configured model IDs.
   */
  export type ModelTier = keyof typeof modelIds;

  // =============================================================================
  // MODEL METADATA
  // =============================================================================

  /**
   * Metadata for each available model.
   */
  export interface ModelMetadata {
    id: string;
    name: string;
    provider: string;
    description: string;
    costTier: "low" | "medium" | "high";
  }

  /**
   * Registry of available models with their metadata.
   * Useful for UI model selectors and documentation.
   * All models are routed through LiteLLM proxy.
   */
  export const MODEL_CATALOG: ModelMetadata[] = [
    {
      id: "openai/gemini-2.5-pro",
      name: "Gemini 2.5 Pro",
      provider: "google",
      description: "Best quality with full tool use support (recommended)",
      costTier: "medium",
    },
    {
      id: "openai/gemini-2.5-flash",
      name: "Gemini 2.5 Flash",
      provider: "google",
      description: "Fast and cost-effective with full tool use support",
      costTier: "low",
    },
  ];

  // =============================================================================
  // CHAT MODEL CATALOG
  // =============================================================================

  /**
   * Chat model entry for the model selector UI.
   * Uses LiteLLM model names (not the "openai/" prefixed Mastra IDs).
   */
  export interface ChatModel {
    /** LiteLLM model name (e.g., "gemini-2.0-flash") */
    id: string;
    /** Human-readable display name */
    name: string;
    /** Model provider — who made the model (Anthropic, OpenAI, Qwen, …). Used for grouping in the UI. */
    provider: string;
    /**
     * Compute provider — who serves the model (Together AI, Fireworks AI,
     * OpenRouter, DeepInfra, …). Only set when it differs from `provider`;
     * absent means the model provider serves it first-party. When the
     * gateway group carries same-weights deployments on MORE than one
     * provider (2026-08-18: llama-4-maverick / llama-4-scout /
     * mistral-large on OpenRouter + AWS Bedrock), the providers are joined
     * with " + " — display surfaces show the combined string and the admin
     * catalog's by-provider view splits on it (catalog-provider-groups.ts).
     */
    computeProvider?: string;
    /**
     * True when the model is served by a provider operating under PRC (China)
     * jurisdiction (AIR-724). The test is the SERVING infrastructure's
     * jurisdiction, not the model's origin — DeepSeek/Qwen/Kimi entries served
     * by US aggregators (Together, Fireworks, Bedrock) are NOT flagged. Drives
     * the "China-based" label and the per-client `prcModelsEnabled` opt-in.
     * Must stay in sync with PRC_HOSTED_MODEL_NAMES in @packages/db/queries
     * (the gateway enforcement source) — drift-guarded by
     * lib/ai/prc-hosted-models.test.ts.
     */
    prcHosted?: true;
    /**
     * True when the serving provider's terms permit training on API inputs
     * with no opt-out (AIR-842): DeepSeek official, Moonshot official,
     * NVIDIA hosted NIM. Serving infrastructure, not model origin — the
     * Together/DeepInfra nemotron legs are NOT flagged. Drives the catalog
     * lock badge and the per-client `trainingModelsEnabled` opt-in
     * (fail-closed). Must stay in sync with TRAINING_GATED_MODEL_NAMES in
     * @packages/db/queries — drift-guarded by
     * lib/ai/data-guarantee-models.test.ts.
     */
    trainingGated?: true;
    /**
     * True when the serving provider's data retention can neither be turned
     * off nor verified (AIR-842): Tensormesh and GMI Cloud legs. Drives the catalog lock
     * badge and the per-client `unverifiedRetentionModelsEnabled` opt-in
     * (fail-closed). Must stay in sync with RETENTION_GATED_MODEL_NAMES in
     * @packages/db/queries — same drift guard as `trainingGated`.
     */
    retentionGated?: true;
    // NOTE deliberately NO `policyGated` flag here (AIR-888, key-flag +
    // never-advertised revision): third-party-policy-gated (adult content)
    // models are API-only and never appear in the web registry at all — a
    // drift guard (third-party-policy-models.test.ts) fails CI if a gated
    // name from @packages/db/queries shows up in CHAT_MODELS.
  }

  /**
   * The default chat model ID (LiteLLM model name).
   */
  export const DEFAULT_CHAT_MODEL_ID = "gpt-5.4-mini";

  /**
   * Cheapest chat-capable model the gateway serves, for internal auxiliary
   * tasks (chat-title generation and similar short, low-stakes calls).
   * gpt-oss-20b via DeepInfra (primary since 2026-09-24, when Together
   * withdrew serverless serving): $0.03 / 1M input, $0.14 / 1M output — see
   * apps/litellm/litellm_config.yaml. Not a user-facing default; do not use
   * for product/customer chat.
   */
  export const CHEAP_TASK_MODEL_ID = "gpt-oss-20b";

  /**
   * Serving provider for CHEAP_TASK_MODEL_ID as stored in `provider_pricing` /
   * `litellm_usage_log` (the proxy attributes gpt-oss-20b to "deepinfra" via
   * MODEL_PROVIDER_OVERRIDES). Used for FMV cost lookup on internal-call
   * telemetry rows.
   */
  export const CHEAP_TASK_MODEL_PROVIDER = "deepinfra";

  /**
   * All chat-eligible models from the LiteLLM proxy config.
   * Excludes embedding models. Grouped by provider for UI display.
   *
   * Model IDs match the `model_name` field in litellm_config.yaml.
   */
  export const CHAT_MODELS = [
    // Google Gemini
    { id: "gemini-3.1-pro-preview", name: "Gemini 3.1 Pro (Preview)", provider: "Google" },
    { id: "gemini-3.8-flash", name: "Gemini 3.8 Flash", provider: "Google" },
    { id: "gemini-3.7-flash", name: "Gemini 3.7 Flash", provider: "Google" },
    { id: "gemini-3.6-flash", name: "Gemini 3.6 Flash", provider: "Google" },
    { id: "gemini-3.5-flash", name: "Gemini 3.5 Flash", provider: "Google" },
    { id: "gemini-3.5-flash-lite", name: "Gemini 3.5 Flash Lite", provider: "Google" },
    { id: "gemini-3.1-flash-lite", name: "Gemini 3.1 Flash Lite", provider: "Google" },
    { id: "gemini-2.5-pro", name: "Gemini 2.5 Pro", provider: "Google" },
    { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash", provider: "Google" },
    // gemini-2.5-flash-lite retired 2026-08-31 (Google 404s it on our keys);
    // DEFAULT_MODEL_ID_MIGRATIONS routes stored defaults to gemini-3.5-flash-lite.
    { id: "gemini-3-flash-preview", name: "Gemini 3 Flash (Preview)", provider: "Google" },
    // OpenAI
    // The gpt-5.6 -pro tiers (sol/terra/luna), gpt-5.2-codex, and
    // gpt-5.1-codex-max were removed 2026-09-01 (AIR-1145 triage): never
    // servable on our keys — the -pro tiers are an OpenAI entitlement gap
    // (ask tracked in AIR-1177), the codex pair 404s with zero prod ledger
    // rows ever. No aliases or migrations (no callers to cover).
    { id: "gpt-6-astra", name: "GPT-6 Astra", provider: "OpenAI" },
    // Ultrafast (2026-10-14): OpenAI's fastest service tier on the same
    // Astra / 6.1 Sol weights, 6x the Standard card. Listed AFTER the base so
    // a bare "use gpt" family pick (same catalog `created` stamp, listing
    // order breaks the tie) resolves to the Standard tier, never the 6x twin.
    { id: "gpt-6-astra-ultrafast", name: "GPT-6 Astra Ultrafast", provider: "OpenAI" },
    // AIR-1494: GPT-6 Sol and 6.1 Sol on our own OpenAI key.
    // The registry-sync lane only saw the OpenRouter/Perplexity resale legs
    // (OPENAI_API_KEY is not a CI secret) — these are the maker legs.
    { id: "gpt-6.1-sol", name: "GPT-6.1 Sol", provider: "OpenAI" },
    { id: "gpt-6.1-sol-ultrafast", name: "GPT-6.1 Sol Ultrafast", provider: "OpenAI" },
    { id: "gpt-6-sol", name: "GPT-6 Sol", provider: "OpenAI" },
    // GPT-6 Luna (2026-10-05): the fast GPT-6 tier, served natively by OpenAI.
    { id: "gpt-6-luna", name: "GPT-6 Luna", provider: "OpenAI" },
    { id: "gpt-5.6-sol", name: "GPT-5.6 Sol", provider: "OpenAI" },
    { id: "gpt-5.6-sol-ultra", name: "GPT-5.6 Sol Ultra", provider: "OpenAI" },
    { id: "gpt-5.6-terra", name: "GPT-5.6 Terra", provider: "OpenAI" },
    { id: "gpt-5.6-luna", name: "GPT-5.6 Luna", provider: "OpenAI" },
    { id: "gpt-5.5", name: "GPT-5.5", provider: "OpenAI" },
    { id: "gpt-5.5-pro", name: "GPT-5.5 Pro", provider: "OpenAI" },
    { id: "gpt-5.4", name: "GPT-5.4", provider: "OpenAI" },
    // AIR-1400 (2026-09-18): current OpenAI ids entitled on our key that the
    // catalog never carried — pro tiers of 5.4/5.2, the 5.2/5.1 generations,
    // and the non-reasoning 4.1 pair. gpt-5-*/o3 (shutdown 2026-12-11) and
    // gpt-4.1-nano (2026-10-23) were deliberately skipped.
    { id: "gpt-5.4-pro", name: "GPT-5.4 Pro", provider: "OpenAI" },
    { id: "gpt-5.4-mini", name: "GPT-5.4 Mini", provider: "OpenAI" },
    { id: "gpt-5.4-nano", name: "GPT-5.4 Nano", provider: "OpenAI" },
    { id: "gpt-5.3-codex", name: "GPT-5.3 Codex", provider: "OpenAI" },
    { id: "gpt-5.2", name: "GPT-5.2", provider: "OpenAI" },
    { id: "gpt-5.2-pro", name: "GPT-5.2 Pro", provider: "OpenAI" },
    { id: "gpt-5.1", name: "GPT-5.1", provider: "OpenAI" },
    { id: "gpt-4o", name: "GPT-4o", provider: "OpenAI" },
    { id: "gpt-4o-mini", name: "GPT-4o Mini", provider: "OpenAI" },
    { id: "gpt-4.1", name: "GPT-4.1", provider: "OpenAI" },
    { id: "gpt-4.1-mini", name: "GPT-4.1 Mini", provider: "OpenAI" },
    // Anthropic
    { id: "claude-fable-5-1", name: "Claude Fable 5.1", provider: "Anthropic" },
    { id: "claude-fable-5", name: "Claude Fable 5", provider: "Anthropic" },
    // The claude-opus-*-fast serving tiers were removed 2026-09-01
    // (AIR-1145 triage): an Anthropic entitlement gap, not a retirement —
    // our org's model list has no -fast ids and the ledger has zero rows
    // ever (ask tracked in AIR-1177).
    { id: "claude-opus-5-5", name: "Claude Opus 5.5", provider: "Anthropic" },
    { id: "claude-opus-5", name: "Claude Opus 5", provider: "Anthropic" },
    { id: "claude-opus-4-8", name: "Claude Opus 4.8", provider: "Anthropic" },
    { id: "claude-opus-4-7", name: "Claude Opus 4.7", provider: "Anthropic" },
    { id: "claude-opus-4-6", name: "Claude Opus 4.6", provider: "Anthropic" },
    { id: "claude-opus-4-5", name: "Claude Opus 4.5", provider: "Anthropic" },
    { id: "claude-sonnet-5-5", name: "Claude Sonnet 5.5", provider: "Anthropic" },
    { id: "claude-sonnet-5", name: "Claude Sonnet 5", provider: "Anthropic" },
    { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", provider: "Anthropic" },
    { id: "claude-sonnet-4-5", name: "Claude Sonnet 4.5", provider: "Anthropic" },
    { id: "claude-haiku-4-5-20251001", name: "Claude Haiku 4.5", provider: "Anthropic" },
    // xAI
    { id: "grok-4-6", name: "Grok 4.6", provider: "xAI" },
    { id: "grok-build-0-1", name: "Grok Build 0.1", provider: "xAI" },
    { id: "grok-4-20", name: "Grok 4.20", provider: "xAI" },
    // Non-reasoning serving of the same 4.20 weights (xAI id
    // grok-4.20-0309-non-reasoning; AIR-1400, 2026-09-18).
    { id: "grok-4-20-non-reasoning", name: "Grok 4.20 (Non-Reasoning)", provider: "xAI" },
    { id: "grok-4-20-multi-agent", name: "Grok 4.20 Multi-Agent", provider: "xAI" },
    { id: "grok-4-5", name: "Grok 4.5", provider: "xAI" },
    { id: "grok-4-3", name: "Grok 4.3", provider: "xAI" },
    { id: "grok-4-1-fast-reasoning", name: "Grok 4.1 Fast (Reasoning)", provider: "xAI" },
    { id: "grok-4-1-fast-non-reasoning", name: "Grok 4.1 Fast", provider: "xAI" },
    { id: "grok-3", name: "Grok 3", provider: "xAI" },
    { id: "grok-3-mini", name: "Grok 3 Mini", provider: "xAI" },
    // Perplexity
    { id: "sonar-pro", name: "Sonar Pro", provider: "Perplexity" },
    { id: "sonar", name: "Sonar", provider: "Perplexity" },
    { id: "sonar-reasoning-pro", name: "Sonar Reasoning Pro", provider: "Perplexity" },
    { id: "sonar-deep-research", name: "Sonar Deep Research", provider: "Perplexity" },
    // Amazon Nova (served via AWS Bedrock — first-party Bedrock-only serving,
    // so these are primary names, not -bedrock variants of a direct twin)
    { id: "nova-2-lite", name: "Nova 2 Lite", provider: "Amazon", computeProvider: "Amazon Bedrock" },
    { id: "nova-pro", name: "Nova Pro", provider: "Amazon", computeProvider: "Amazon Bedrock" },
    { id: "nova-lite", name: "Nova Lite", provider: "Amazon", computeProvider: "Amazon Bedrock" },
    { id: "nova-micro", name: "Nova Micro", provider: "Amazon", computeProvider: "Amazon Bedrock" },
    // Meta Llama (multi-provider same-weights serving, 2026-08-18: the
    // gateway group carries an OpenRouter leg AND an AWS Bedrock leg of the
    // identical weights — " + " joins the serving providers; the admin
    // catalog splits on it (catalog-provider-groups.ts) so the model lists
    // under both serving groups)
    { id: "llama-4-maverick", name: "Llama 4 Maverick", provider: "Meta", computeProvider: "OpenRouter + Amazon Bedrock" },
    { id: "llama-4-scout", name: "Llama 4 Scout", provider: "Meta", computeProvider: "OpenRouter + Amazon Bedrock" },
    // muse-spark-1.2 AND -1.3 delisted/withheld: OpenRouter 403s the Muse
    // Spark family behind an account-level 18+ attestation we have not
    // completed (1.3 re-verified 2026-09-04) — never servable on our
    // account. Re-add after the AIR-888/AIR-1131 attestation decision.
    // Mistral (mistral-large: same OpenRouter + Bedrock multi-leg serving as
    // the Llama entries above). mistral-small retired 2026-09-01 (AIR-1145
    // triage — the one TRUE retirement of the batch, it HAD served):
    // OpenRouter lost its last mistral-small-creative endpoint and no
    // same-variant successor exists; stale callers get the retired-model
    // error copy (RETIRED_MODEL_IDS).
    { id: "mistral-large", name: "Mistral Large", provider: "Mistral", computeProvider: "OpenRouter + Amazon Bedrock" },
    // Reflection Beam 1.0 (501B-A23B MoE; 2026-10-20 registry sync, merged
    // 2026-10-26). Gateway id is the family name `beam-1.0` (the kimi-k3 /
    // glm-5.3 short-id convention — OpenRouter's catalog slug carries the
    // size, the gateway id does not); Baseten's leg rides as beam-1.0-baseten.
    // The unsuffixed id is the Together AI direct leg (aggregator-primary
    // convention, the glm-5.3 pattern). The OpenRouter resale leg
    // (beam-1.0-openrouter) is engine-only, the
    // llama-4-*-openrouter pinned-route shape — OpenRouter has no serving
    // label of its own, so the leg can't be a picker/catalog sibling.
    { id: "beam-1.0", name: "Reflection Beam 1.0", provider: "Reflection", computeProvider: "Together AI" },
    // Mistral La Plateforme (the maker API — first-party serving, unlike the
    // OpenRouter-served Mistral entries above)
    { id: "mistral-medium-3.5", name: "Mistral Medium 3.5", provider: "Mistral" },
    { id: "codestral-2508", name: "Codestral", provider: "Mistral" },
    { id: "ministral-14b", name: "Ministral 14B", provider: "Mistral" },
    // AIR-1400 (2026-09-18): the rest of the current La Plateforme lineup.
    { id: "mistral-small-4", name: "Mistral Small 4", provider: "Mistral" },
    { id: "ministral-8b", name: "Ministral 8B", provider: "Mistral" },
    { id: "ministral-3b", name: "Ministral 3B", provider: "Mistral" },
    // DeepSeek (official api.deepseek.com — PRC-jurisdiction serving)
    { id: "deepseek-chat", name: "DeepSeek Chat", provider: "DeepSeek", prcHosted: true, trainingGated: true },
    { id: "deepseek-reasoner", name: "DeepSeek Reasoner", provider: "DeepSeek", prcHosted: true, trainingGated: true },
    // DeepSeek's official `deepseek-flash` endpoint serves the V4.1 Flash
    // launch weights. It is distinct from our legacy US-hosted V4 Flash ids.
    { id: "deepseek-flash", name: "DeepSeek V4.1 Flash (Official)", provider: "DeepSeek", prcHosted: true, trainingGated: true },
    // Open-weight models served by Together AI
    { id: "qwen-3.7-max", name: "Qwen 3.7 Max", provider: "Qwen", computeProvider: "Together AI" },
    { id: "deepseek-v4-pro", name: "DeepSeek V4 Pro", provider: "DeepSeek", computeProvider: "Together AI" },
    // kimi-k2.7-code retired 2026-09-01 (Together withdrew serving, AIR-1187);
    // DEFAULT_MODEL_ID_MIGRATIONS routes stored defaults to the maker leg
    // kimi-k2.7-code-moonshot.
    { id: "minimax-m3", name: "MiniMax M3", provider: "MiniMax", computeProvider: "Together AI" },
    // minimax-m2.7 retired 2026-09-01 (same withdrawal, AIR-1187) with NO
    // successor — the gateway answers the old id with retirement copy.
    // minimax-m2.5 retired 2026-09-01 (same withdrawal); stored defaults
    // migrate to the retention-gated minimax-m2.5-tensormesh leg.
    { id: "gpt-oss-20b", name: "GPT-OSS 20B", provider: "OpenAI", computeProvider: "DeepInfra" },
    { id: "muse-glimmer", name: "Muse Glimmer 30B", provider: "Meta", computeProvider: "Together AI" },
    // AIR-826 Together serving legs of models whose primary lives elsewhere.
    // Parenthetical naming per the AIR-738 multi-leg display convention.
    // kimi-k2.6-together retired 2026-09-01 (Together withdrew serving,
    // AIR-1187); stored defaults migrate to kimi-k2.6-moonshot. The
    // Fireworks primary kimi-k2.6 is unaffected.
    { id: "glm-5.2-together", name: "GLM 5.2 (Together)", provider: "Z.ai", computeProvider: "Together AI" },
    // nemotron-3-ultra-together retired 2026-09-01 (same withdrawal);
    // stored defaults migrate to the DeepInfra primary nemotron-3-ultra.
    // AIR-910 Together V4-Flash legs (dated -0731 snapshot upstream); the
    // reasoner twin bakes thinking into the name like deepseek-reasoner.
    { id: "deepseek-v4-flash-together", name: "DeepSeek V4 Flash (Together)", provider: "DeepSeek", computeProvider: "Together AI" },
    { id: "deepseek-v4-flash-reasoner-together", name: "DeepSeek V4 Flash Reasoner (Together)", provider: "DeepSeek", computeProvider: "Together AI" },
    // Open-weight models served by Fireworks AI
    // glm-5 and glm-5.1 removed 2026-09-01 (AIR-1145 triage): never
    // deployed on our Fireworks account (zero ledger rows ever); glm-5.3
    // serves the family. No aliases or migrations.
    { id: "glm-5.3", name: "GLM 5.3", provider: "Z.ai", computeProvider: "Fireworks AI" },
    { id: "glm-5.2", name: "GLM 5.2", provider: "Z.ai", computeProvider: "DeepInfra" },
    { id: "qwen-3.7-plus", name: "Qwen 3.7 Plus", provider: "Qwen", computeProvider: "Fireworks AI" },
    { id: "kimi-k2.6", name: "Kimi K2.6", provider: "Moonshot", computeProvider: "DeepInfra" },
    { id: "deepseek-v4-flash", name: "DeepSeek V4 Flash", provider: "DeepSeek", computeProvider: "DeepInfra" },
    // Thinking-mode twin of deepseek-v4-flash (2026-08-14): the US-hosted
    // mirror of the official deepseek-reasoner alias — mode baked into the
    // name, no client-side knob.
    { id: "deepseek-v4-flash-reasoner", name: "DeepSeek V4 Flash Reasoner", provider: "DeepSeek", computeProvider: "DeepInfra" },
    { id: "gpt-oss-120b", name: "GPT-OSS 120B", provider: "OpenAI", computeProvider: "Fireworks AI" },
    // AIR-826 Fireworks serving legs. kimi-k3-fireworks is the first
    // US-hosted K3 route (the kimi-k3 primary below is Moonshot official).
    { id: "kimi-k3-fireworks", name: "Kimi K3 (Fireworks)", provider: "Moonshot", computeProvider: "Fireworks AI" },
    // Ember-1 (2026-10-16): Fireworks Research's own post-train of Kimi K3
    // for token efficiency (~40% shorter reasoning traces), Research Preview
    // on Fireworks serverless. Fireworks is maker AND server, so the maker
    // label is Fireworks AI; different weights from the K3 legs (no alias,
    // no chain). Same $3 / $15 card and 1M window as kimi-k3-fireworks.
    { id: "ember-1", name: "Ember-1", provider: "Fireworks AI", computeProvider: "Fireworks AI" },
    // gpt-oss-20b-fireworks removed 2026-09-01 (AIR-1145 triage): never
    // deployed on our Fireworks account (zero ledger rows ever); the
    // Together primary gpt-oss-20b serves the weights. No alias.
    // AIR-910: V4-Pro serving leg on Fireworks (dated -0813 official
    // snapshot; thinking-on by default like every V4-Pro leg).
    // DeepSeek V4 Flash Vision Exp serving leg on Fireworks (2026-09-07):
    // same experimental vision weights as the Novita leg, half its price —
    // AIR-738 parenthetical naming, chain-free until AIR-503 verification.
    // Open-weight models served by DeepInfra (AIR-738/AIR-826). Display names
    // carry a parenthetical because these deployments share a base model with
    // another provider's leg — every derived surface (picker trigger, e2e
    // titles) stays unambiguous.
    { id: "deepseek-v4-flash-deepinfra", name: "DeepSeek V4 Flash (DeepInfra)", provider: "DeepSeek", computeProvider: "DeepInfra" },
    // DeepInfra is the unsuffixed US-hosted primary for the V4.1 Flash
    // release; the separate `deepseek-flash` entry is the official PRC leg.
    { id: "deepseek-v4.1-flash", name: "DeepSeek V4.1 Flash", provider: "DeepSeek", computeProvider: "DeepInfra" },
    // V4.1 Flash 1017 (DeepSeek's 2026-10-17 checkpoint — a different model
    // from deepseek-v4.1-flash, no alias between them). Fireworks is the
    // unsuffixed primary; added for Nectar.
    { id: "deepseek-v4.1-flash-1017", name: "DeepSeek V4.1 Flash 1017", provider: "DeepSeek", computeProvider: "Fireworks AI" },
    // Baseten Fast serving tier of the 1017 checkpoint (2026-10-18): premium
    // serving twin (FAST_SERVING_TWIN_IDS) — a bare family word never
    // auto-picks it over the Fireworks primary.
    { id: "deepseek-v4.1-flash-1017-fast", name: "DeepSeek V4.1 Flash 1017 Fast", provider: "DeepSeek", computeProvider: "Baseten" },
    // Thinking-mode twin of deepseek-v4-flash-deepinfra (2026-08-14): mode
    // baked into the name like deepseek-reasoner, no client-side knob.
    { id: "deepseek-v4-flash-reasoner-deepinfra", name: "DeepSeek V4 Flash Reasoner (DeepInfra)", provider: "DeepSeek", computeProvider: "DeepInfra" },
    // DeepSeek V4 Flash Vision Exp serving leg on DeepInfra (2026-09-07):
    // same experimental vision weights as the Novita leg, roughly half its
    // price — chain-free until AIR-503 verification.
    { id: "deepseek-v4-flash-vision-exp-deepinfra", name: "DeepSeek V4 Flash Vision Exp (DeepInfra)", provider: "DeepSeek", computeProvider: "DeepInfra" },
    // DeepSeek V3.2 (2026-09-10): unsuffixed DeepInfra-served primary (the
    // glm-5.3 pattern) — no parenthetical; siblings carry suffixes.
    { id: "deepseek-v3.2", name: "DeepSeek V3.2", provider: "DeepSeek", computeProvider: "DeepInfra" },
    { id: "qwen-3.8-max-deepinfra", name: "Qwen 3.8 Max (DeepInfra)", provider: "Qwen", computeProvider: "DeepInfra" },
    // GLM-5.3 serving leg on DeepInfra (2026-09-02): same weights as the
    // Fireworks primary, cheaper serving — AIR-738 parenthetical naming.
    { id: "glm-5.3-deepinfra", name: "GLM 5.3 (DeepInfra)", provider: "Z.ai", computeProvider: "DeepInfra" },
    { id: "glm-5.2-deepinfra", name: "GLM 5.2 (DeepInfra)", provider: "Z.ai", computeProvider: "DeepInfra" },
    // GLM-4.7 DeepInfra serving leg (2026-09-11): same weights as the
    // glm-4.7-novita primary, ~200K context, text-only.
    { id: "glm-4.7-deepinfra", name: "GLM 4.7 (DeepInfra)", provider: "Z.ai", computeProvider: "DeepInfra" },
    { id: "deepseek-v4-pro-deepinfra", name: "DeepSeek V4 Pro (DeepInfra)", provider: "DeepSeek", computeProvider: "DeepInfra" },
    { id: "kimi-k2.7-code-deepinfra", name: "Kimi K2.7 Code (DeepInfra)", provider: "Moonshot", computeProvider: "DeepInfra" },
    { id: "kimi-k2.6-deepinfra", name: "Kimi K2.6 (DeepInfra)", provider: "Moonshot", computeProvider: "DeepInfra" },
    { id: "minimax-m3-deepinfra", name: "MiniMax M3 (DeepInfra)", provider: "MiniMax", computeProvider: "DeepInfra" },
    { id: "gpt-oss-20b-deepinfra", name: "GPT-OSS 20B (DeepInfra)", provider: "OpenAI", computeProvider: "DeepInfra" },
    { id: "gpt-oss-120b-deepinfra", name: "GPT-OSS 120B (DeepInfra)", provider: "OpenAI", computeProvider: "DeepInfra" },
    { id: "llama-4-maverick-deepinfra", name: "Llama 4 Maverick (DeepInfra)", provider: "Meta", computeProvider: "DeepInfra" },
    { id: "llama-4-scout-deepinfra", name: "Llama 4 Scout (DeepInfra)", provider: "Meta", computeProvider: "DeepInfra" },
    { id: "nemotron-3-nano-deepinfra", name: "Nemotron 3 Nano (DeepInfra)", provider: "NVIDIA", computeProvider: "DeepInfra" },
    { id: "llama-3.3-70b", name: "Llama 3.3 70B", provider: "Meta", computeProvider: "DeepInfra" },
    // Open-weight models served by Novita AI (AIR-738/AIR-826)
    // Novita retires deepseek/deepseek-v4-flash on 2026-10-09 16:00 UTC
    // (https://docs.novita.ai/changelog/28-09-26): the -novita V4 Flash legs
    // below leave the catalog at the cutoff (AIR-795 notice in flight).
    { id: "deepseek-v4-flash-novita", name: "DeepSeek V4 Flash (Novita)", provider: "DeepSeek", computeProvider: "Novita AI" },
    // Thinking-mode twin of deepseek-v4-flash-novita (AIR-910): mode baked
    // into the name like deepseek-reasoner, no client-side knob.
    { id: "deepseek-v4-flash-reasoner-novita", name: "DeepSeek V4 Flash Reasoner (Novita)", provider: "DeepSeek", computeProvider: "Novita AI" },
    // DeepSeek V4.1 Flash on Novita (2026-09-29): Novita's named successor
    // to its retiring V4 Flash — a DIFFERENT model, never a silent substitute
    // (DC-11). Same weights as the DeepInfra-served deepseek-v4.1-flash.
    { id: "deepseek-v4.1-flash-novita", name: "DeepSeek V4.1 Flash (Novita)", provider: "DeepSeek", computeProvider: "Novita AI" },
    { id: "qwen-3.8-max-novita", name: "Qwen 3.8 Max (Novita)", provider: "Qwen", computeProvider: "Novita AI" },
    { id: "glm-5.2-novita", name: "GLM 5.2 (Novita)", provider: "Z.ai", computeProvider: "Novita AI" },
    // GLM-5.3-Flash (2026-08-26): natively multimodal (image in, text out),
    // 1M window, MIT-licensed. Novita is the only onboarded host at launch.
    { id: "glm-5.3-flash-novita", name: "GLM 5.3 Flash (Novita)", provider: "Z.ai", computeProvider: "Novita AI" },
    // GLM-4.7 (2026-09-11): Z.ai flagship, ~200K context, text-only. No
    // unsuffixed leg — Novita is the suffixed primary (glm-5.3-flash-novita
    // precedent); same-weights -deepinfra / -baseten twins.
    { id: "glm-4.7-novita", name: "GLM 4.7 (Novita)", provider: "Z.ai", computeProvider: "Novita AI" },
    // DeepSeek V4 Flash Vision Exp (2026-08-31 registry sync): experimental
    // vision-enabled variant of the V4-Flash base — distinct weights, own
    // group. Novita is the only onboarded serving; the DeepSeek-official leg
    // is PRC-hosted + training-gated and deliberately not added.
    { id: "deepseek-v4-flash-vision-exp-novita", name: "DeepSeek V4 Flash Vision Exp (Novita)", provider: "DeepSeek", computeProvider: "Novita AI" },
    { id: "deepseek-v4-pro-novita", name: "DeepSeek V4 Pro (Novita)", provider: "DeepSeek", computeProvider: "Novita AI" },
    // deepseek-v3.2-novita left 2026-09-29 — Novita no longer serves V3.2
    // (RETIRED_MODEL_ALIASES → deepseek-v3.2).
    { id: "kimi-k2.7-code-novita", name: "Kimi K2.7 Code (Novita)", provider: "Moonshot", computeProvider: "Novita AI" },
    { id: "kimi-k2.6-novita", name: "Kimi K2.6 (Novita)", provider: "Moonshot", computeProvider: "Novita AI" },
    { id: "minimax-m3-novita", name: "MiniMax M3 (Novita)", provider: "MiniMax", computeProvider: "Novita AI" },
    { id: "gpt-oss-20b-novita", name: "GPT-OSS 20B (Novita)", provider: "OpenAI", computeProvider: "Novita AI" },
    { id: "llama-4-maverick-novita", name: "Llama 4 Maverick (Novita)", provider: "Meta", computeProvider: "Novita AI" },
    { id: "llama-4-scout-novita", name: "Llama 4 Scout (Novita)", provider: "Meta", computeProvider: "Novita AI" },
    // Open-weight models served by Baseten (AIR-839; BASETEN_API_KEY live
    // since 2026-08-18). The two Llama-4 legs were removed in AIR-950 —
    // dead upstream (Baseten delisted their Model APIs, slugs 404'd; probed
    // 2026-08-20, AIR-932), never served a request.
    { id: "gpt-oss-120b-baseten", name: "GPT-OSS 120B (Baseten)", provider: "OpenAI", computeProvider: "Baseten" },
    { id: "deepseek-v4-flash-baseten", name: "DeepSeek V4 Flash (Baseten)", provider: "DeepSeek", computeProvider: "Baseten" },
    // Thinking-mode twin of deepseek-v4-flash-baseten: mode baked into the
    // name like deepseek-reasoner, no client-side knob.
    { id: "deepseek-v4-flash-reasoner-baseten", name: "DeepSeek V4 Flash Reasoner (Baseten)", provider: "DeepSeek", computeProvider: "Baseten" },
    // Same GLM-5.3-Flash weights as the Novita primary leg (2026-08-26),
    // second US host (2026-09-10).
    { id: "glm-5.3-flash-baseten", name: "GLM 5.3 Flash (Baseten)", provider: "Z.ai", computeProvider: "Baseten" },
    // AIR-1300 Baseten catalog completion (2026-09-11): same-weights Baseten
    // serving legs of already-cataloged groups — slugs and rates verified on
    // Baseten's own library pages.
    { id: "glm-5.3-baseten", name: "GLM 5.3 (Baseten)", provider: "Z.ai", computeProvider: "Baseten" },
    { id: "glm-5.2-baseten", name: "GLM 5.2 (Baseten)", provider: "Z.ai", computeProvider: "Baseten" },
    { id: "deepseek-v4-pro-baseten", name: "DeepSeek V4 Pro (Baseten)", provider: "DeepSeek", computeProvider: "Baseten" },
    { id: "kimi-k2.6-baseten", name: "Kimi K2.6 (Baseten)", provider: "Moonshot", computeProvider: "Baseten" },
    { id: "kimi-k2.7-code-baseten", name: "Kimi K2.7 Code (Baseten)", provider: "Moonshot", computeProvider: "Baseten" },
    // Baseten leg of Reflection Beam 1.0 (2026-10-26) — same weights as the
    // Together primary `beam-1.0` (Baseten serves the NVFP4 build).
    { id: "beam-1.0-baseten", name: "Reflection Beam 1.0 (Baseten)", provider: "Reflection", computeProvider: "Baseten" },
    // (glm-4.7-baseten retired 2026-10-05 — Baseten 410 Gone; stored
    // defaults migrate to the same-weights glm-4.7-novita primary via
    // DEFAULT_MODEL_ID_MIGRATIONS. The primary and the glm-4.7-deepinfra
    // twin above are unaffected.)
    // DeepSeek V4.1 Flash Fast (2026-10-03 registry sync): Baseten's speed
    // tier of V4.1 Flash — its own model, not a serving leg of the
    // deepseek-v4.1-flash base (DC-11). Baseten is the only onboarded host,
    // so the id is unsuffixed (no "(Baseten)" parenthetical — the
    // glm-5.3-flash-novita suffixed-primary convention doesn't apply to a
    // distinct-model id; the by-provider catalog view still groups it under
    // Baseten via computeProvider).
    { id: "deepseek-v4.1-flash-fast", name: "DeepSeek V4.1 Flash Fast", provider: "DeepSeek", computeProvider: "Baseten" },
    // NVIDIA open-weight models served by DeepInfra (AIR-810 rerouted them
    // off NVIDIA's hosted NIM catalog, whose terms permit training on inputs)
    // — green serving, so the AIR-842 training gate does NOT flag them.
    { id: "nemotron-3.5-lightning", name: "Nemotron 3.5 Lightning", provider: "NVIDIA", computeProvider: "DeepInfra" },
    { id: "nemotron-3-super", name: "Nemotron 3 Super", provider: "NVIDIA", computeProvider: "DeepInfra" },
    { id: "nemotron-3-nano", name: "Nemotron 3 Nano", provider: "NVIDIA", computeProvider: "DeepInfra" },
    { id: "nemotron-3-ultra", name: "Nemotron 3 Ultra", provider: "NVIDIA", computeProvider: "DeepInfra" },
    // NVIDIA's own hosted NIM serving, re-added as OPT-IN legs (AIR-869 —
    // "more options, not less"): the NIM API-catalog trial terms permit
    // training on inputs/outputs, so these carry the AIR-842 training gate
    // and unlock only after the org's trainingModelsEnabled consent. Ultra
    // gets its full 1M window here vs the aggregator pins.
    // (nemotron-3-nano-nvidia retired 2026-09-01 and nemotron-3-super-nvidia
    // 2026-10-03 — NVIDIA 410 Gone, no successor; the DeepInfra-served
    // nemotron-3-nano / nemotron-3-super above are unaffected.)
    { id: "nemotron-3-ultra-nvidia", name: "Nemotron 3 Ultra (NVIDIA)", provider: "NVIDIA", computeProvider: "NVIDIA NIM", trainingGated: true },
    // Open-weight models served by Tensormesh (AIR-834; catalog completed
    // AIR-964) — LMCache-backed serving with $0 cached input tokens.
    { id: "kimi-k2.6-tensormesh", name: "Kimi K2.6 (Tensormesh)", provider: "Moonshot", computeProvider: "Tensormesh", retentionGated: true },
    { id: "glm-5.2-tensormesh", name: "GLM 5.2 (Tensormesh)", provider: "Z.ai", computeProvider: "Tensormesh", retentionGated: true },
    { id: "qwen-3.5-397b", name: "Qwen 3.5 397B", provider: "Qwen", computeProvider: "Tensormesh", retentionGated: true },
    { id: "qwen3-coder-480b", name: "Qwen3 Coder 480B", provider: "Qwen", computeProvider: "Tensormesh", retentionGated: true },
    { id: "minimax-m2.5-tensormesh", name: "MiniMax M2.5 (Tensormesh)", provider: "MiniMax", computeProvider: "Tensormesh", retentionGated: true },
    { id: "deepseek-v4-flash-tensormesh", name: "DeepSeek V4 Flash (Tensormesh)", provider: "DeepSeek", computeProvider: "Tensormesh", retentionGated: true },
    { id: "gpt-oss-120b-tensormesh", name: "GPT-OSS 120B (Tensormesh)", provider: "OpenAI", computeProvider: "Tensormesh", retentionGated: true },
    { id: "gpt-oss-20b-tensormesh", name: "GPT-OSS 20B (Tensormesh)", provider: "OpenAI", computeProvider: "Tensormesh", retentionGated: true },
    { id: "qwen-3.6-27b", name: "Qwen 3.6 27B", provider: "Qwen", computeProvider: "Tensormesh", retentionGated: true },
    { id: "gemma-4-31b", name: "Gemma 4 31B", provider: "Google", computeProvider: "Tensormesh", retentionGated: true },
    // Open-weight models served by StreamLake (Kuaishou's Vanchin intl
    // platform, AIR-1302 — generic openai/+api_base serving like Tensormesh).
    // EVERY leg is PRC-gated (operator's parent is Kuaishou, PRC-headquartered;
    // cross-border transfers to group entities permitted; inference hosting
    // location unstated) AND training-gated (intl ToS §4.6 grants StreamLake a
    // license to use Input and Output to train its models, no published
    // opt-out) — both org consents required, mirroring the deepseek-chat /
    // kimi-k3 double gate.
    { id: "deepseek-v3.2-streamlake", name: "DeepSeek V3.2 (StreamLake)", provider: "DeepSeek", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "deepseek-v4-flash-streamlake", name: "DeepSeek V4 Flash (StreamLake)", provider: "DeepSeek", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "deepseek-v4-pro-streamlake", name: "DeepSeek V4 Pro (StreamLake)", provider: "DeepSeek", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "glm-5.2-streamlake", name: "GLM 5.2 (StreamLake)", provider: "Z.ai", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "kimi-k2.6-streamlake", name: "Kimi K2.6 (StreamLake)", provider: "Moonshot", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "kimi-k2.7-code-streamlake", name: "Kimi K2.7 Code (StreamLake)", provider: "Moonshot", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    { id: "minimax-m2.5-streamlake", name: "MiniMax M2.5 (StreamLake)", provider: "MiniMax", computeProvider: "StreamLake", prcHosted: true, trainingGated: true },
    // The ByteDance Ark chat legs (glm-5.2 / glm-5.3-flash / deepseek-v4-flash
    // / deepseek-v4-pro "-bytedance", computeProvider "ByteDance", PRC +
    // retention gated; PR #1396, 2026-09-16) were pulled 2026-09-17
    // (AIR-1368): BYTEDANCE_ARK_API_KEY was not minted in UAT or prod, so the
    // legs 401'd on the UAT baseline and blocked the production preflight.
    // The key landed 2026-09-22 (the seedance video adapter serves on it);
    // the chat model ids still answer ModelNotOpen for the account, so re-add
    // by reverting the AIR-1368 PR only after they are activated — the
    // PRICING_PROVIDER_BY_COMPUTE_PROVIDER `satisfies` clause below re-admits
    // the "ByteDance" key with the first returning leg.
    // Open-weight models served by GMI Cloud (AIR-1301; US company, San Jose
    // CA, native litellm gmi/ provider on api.gmi-serving.com). All eight are
    // same-weights serving legs of existing catalog groups — several are the
    // cheapest US serving of their weights at onboarding (V4-Flash
    // $0.091/$0.182 vs Baseten $0.13/$0.26). GMI publishes NO API-data
    // training/retention statement at all (disclosed as "Not published" on
    // the data-handling page), so all eight carry the AIR-842 retention gate
    // (RETENTION_GATED_MODEL_NAMES) and unlock only after the org's
    // unverifiedRetentionModelsEnabled consent — same posture as Tensormesh.
    // deepseek-v3.2-gmicloud left 2026-09-29 (GMI Cloud no longer serves
    // V3.2; RETIRED_MODEL_ALIASES → deepseek-v3.2) — seven legs remain.
    { id: "deepseek-v4-flash-gmicloud", name: "DeepSeek V4 Flash (GMI Cloud)", provider: "DeepSeek", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "deepseek-v4-pro-gmicloud", name: "DeepSeek V4 Pro (GMI Cloud)", provider: "DeepSeek", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "glm-5.2-gmicloud", name: "GLM 5.2 (GMI Cloud)", provider: "Z.ai", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "glm-5.3-gmicloud", name: "GLM 5.3 (GMI Cloud)", provider: "Z.ai", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "glm-5.3-flash-gmicloud", name: "GLM 5.3 Flash (GMI Cloud)", provider: "Z.ai", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "kimi-k2.6-gmicloud", name: "Kimi K2.6 (GMI Cloud)", provider: "Moonshot", computeProvider: "GMI Cloud", retentionGated: true },
    { id: "kimi-k2.7-code-gmicloud", name: "Kimi K2.7 Code (GMI Cloud)", provider: "Moonshot", computeProvider: "GMI Cloud", retentionGated: true },
    // Open-weight models served by Sail Research (2026-10-30;
    // api.sailresearch.com, generic openai/+api_base serving like Tensormesh).
    // Zero data retention by default for inference and no training without
    // written consent (published security page + DPA), so NO consent gate —
    // the Fireworks/Baseten posture. Processing region is not fixed (Sail
    // routes across providers in multiple regions; US-only routing is a paid
    // per-request option we do not send) — model-jurisdictions carries every
    // leg as XX. Seven are same-weights serving legs of existing groups;
    // gemma-4-12b and gemma-4-31b-nvfp4 are Sail-only ids. All priced at
    // Sail's default `asap` completion window.
    { id: "kimi-k3-sail", name: "Kimi K3 (Sail)", provider: "Moonshot", computeProvider: "Sail Research" },
    { id: "glm-5.3-sail", name: "GLM 5.3 (Sail)", provider: "Z.ai", computeProvider: "Sail Research" },
    { id: "glm-5.3-flash-sail", name: "GLM 5.3 Flash (Sail)", provider: "Z.ai", computeProvider: "Sail Research" },
    { id: "deepseek-v4.1-flash-1017-sail", name: "DeepSeek V4.1 Flash 1017 (Sail)", provider: "DeepSeek", computeProvider: "Sail Research" },
    { id: "deepseek-v4-pro-sail", name: "DeepSeek V4 Pro (Sail)", provider: "DeepSeek", computeProvider: "Sail Research" },
    { id: "deepseek-v4-flash-sail", name: "DeepSeek V4 Flash (Sail)", provider: "DeepSeek", computeProvider: "Sail Research" },
    { id: "gemma-4-31b-sail", name: "Gemma 4 31B (Sail)", provider: "Google", computeProvider: "Sail Research" },
    { id: "gemma-4-31b-nvfp4", name: "Gemma 4 31B NVFP4", provider: "Google", computeProvider: "Sail Research" },
    { id: "gemma-4-12b", name: "Gemma 4 12B", provider: "Google", computeProvider: "Sail Research" },
    // Moonshot (Kimi) — official Moonshot API (PRC-jurisdiction serving),
    // unlike the US-hosted kimi-k2.x entries above. The AIR-826 -moonshot
    // legs are maker-API twins of the US-hosted K2.x names.
    { id: "kimi-k3", name: "Kimi K3", provider: "Moonshot", prcHosted: true, trainingGated: true },
    { id: "kimi-k2.7-code-moonshot", name: "Kimi K2.7 Code (Moonshot)", provider: "Moonshot", prcHosted: true, trainingGated: true },
    { id: "kimi-k2.6-moonshot", name: "Kimi K2.6 (Moonshot)", provider: "Moonshot", prcHosted: true, trainingGated: true },
    { id: "kimi-k2.5", name: "Kimi K2.5", provider: "Moonshot", prcHosted: true, trainingGated: true },
    { id: "kimi-k2-thinking", name: "Kimi K2 Thinking", provider: "Moonshot", prcHosted: true, trainingGated: true },
    // { id: "moonshot-v1-8k", name: "Moonshot v1 8K", provider: "Moonshot", prcHosted: true },
    // { id: "moonshot-v1-32k", name: "Moonshot v1 32K", provider: "Moonshot", prcHosted: true },
    // { id: "moonshot-v1-128k", name: "Moonshot v1 128K", provider: "Moonshot", prcHosted: true },
    // DeepSeek official API maker leg of the Together-served V4 Pro (AIR-826).
    { id: "deepseek-v4-pro-deepseek", name: "DeepSeek V4 Pro (DeepSeek)", provider: "DeepSeek", prcHosted: true, trainingGated: true },
    // Alibaba Cloud Model Studio — the official Qwen API plus 3P serving legs
    // (AIR-826). PRC-jurisdiction OPERATOR (AIR-724 label + opt-in apply)
    // even though the gateway routes via the US (Virginia) regional
    // endpoint. US serving variants of the same base models live under
    // DeepInfra / Novita AI / Together AI / Fireworks AI above.
    { id: "qwen-3.8-max", name: "Qwen 3.8 Max", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    // Qwen3.8 Flash (2026-08-26 launch): natively multimodal (image/video in),
    // 1M window — same PRC-jurisdiction stance as the rest of the DashScope batch.
    { id: "qwen-3.8-flash", name: "Qwen 3.8 Flash", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3.7-flash", name: "Qwen 3.7 Flash", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3.6-plus", name: "Qwen 3.6 Plus", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3.6-flash", name: "Qwen 3.6 Flash", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    // qwen-3-max-thinking removed 2026-09-01 (AIR-1145 triage): never
    // servable — no distinct upstream id exists (thinking is a param-mode
    // of qwen3-max; re-add tracked in AIR-1177).
    { id: "qwen-3-coder-plus", name: "Qwen 3 Coder Plus", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    // Route-fixed 2026-09-01 (AIR-1145 triage): now serves from DashScope's
    // intl plane — the US plane never carried the id.
    { id: "qwen-3-coder-next", name: "Qwen 3 Coder Next", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3-vl-235b-a22b", name: "Qwen 3 VL 235B", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3.7-max-alibaba", name: "Qwen 3.7 Max (Alibaba)", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "qwen-3.7-plus-alibaba", name: "Qwen 3.7 Plus (Alibaba)", provider: "Qwen", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "glm-5.2-alibaba", name: "GLM 5.2 (Alibaba)", provider: "Z.ai", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "deepseek-v4-pro-alibaba", name: "DeepSeek V4 Pro (Alibaba)", provider: "DeepSeek", computeProvider: "Alibaba Cloud", prcHosted: true },
    { id: "kimi-k2.7-code-alibaba", name: "Kimi K2.7 Code (Alibaba)", provider: "Moonshot", computeProvider: "Alibaba Cloud", prcHosted: true },
  ] as const satisfies readonly ChatModel[];

  /**
   * Curated coding-model subset we recommend GitHub Copilot BYOK admins expose
   * in their org's model picker (AIR-543). Deliberately NOT the full CHAT_MODELS
   * catalog — that includes image/video/search models that would break or
   * confuse the Copilot picker. IDs must match litellm_config.yaml exactly.
   */
  export const RECOMMENDED_COPILOT_MODEL_IDS = [
    "claude-sonnet-4-6",
    "claude-opus-4-8",
    "gpt-5.5",
    "gpt-5.4-mini",
    "gemini-2.5-pro",
  ] as const;

  /**
   * Curated coding preset the IDE connect pickers (Zed, Cline — AIR-609)
   * select by default: the Copilot subset plus the flagship and the
   * coding-specialized open-weight entry. Derived from
   * RECOMMENDED_COPILOT_MODEL_IDS so the two curated coding lists can't
   * drift when models launch or retire.
   */
  export const RECOMMENDED_CODING_MODEL_IDS: readonly string[] = [
    "claude-fable-5",
    ...RECOMMENDED_COPILOT_MODEL_IDS,
    // The coding-specialized open-weight slot moved to the Moonshot maker
    // leg when Together withdrew kimi-k2.7-code serving (AIR-1187,
    // 2026-09-01). NOTE: this leg is PRC-hosted + training-gated — orgs see
    // it in the preset only once their admin has granted both consents
    // (picker gating keys on the id like any other pick).
    "kimi-k2.7-code-moonshot",
  ];

  /** Group provider-tagged models for UI display, preserving input order. */
  export function groupByProvider<T extends { provider: string }>(
    models: readonly T[],
  ): Map<string, T[]> {
    const groups = new Map<string, T[]>();
    for (const model of models) {
      const existing = groups.get(model.provider) || [];
      existing.push(model);
      groups.set(model.provider, existing);
    }
    return groups;
  }

  /**
   * Models grouped by model provider (maker) for UI dropdowns.
   */
  export function getModelsByProvider() {
    return groupByProvider<ChatModel>(CHAT_MODELS);
  }

  /**
   * Compute provider serving a chat/gateway model id, when it differs from the
   * model provider. Undefined for first-party-served models and unknown ids.
   */
  const CHAT_MODEL_BY_ID = new Map<string, ChatModel>(CHAT_MODELS.map((m) => [m.id, m]));

  export function getComputeProvider(modelId: string): string | undefined {
    return CHAT_MODEL_BY_ID.get(modelId)?.computeProvider;
  }

  /** Catalog entry for a chat/gateway model id, or undefined for unknown ids. */
  export function getChatModel(modelId: string): ChatModel | undefined {
    return CHAT_MODEL_BY_ID.get(modelId);
  }

  /**
   * Chat-catalog ids flagged China-hosted (PRC-jurisdiction serving, AIR-724).
   * Merged into the effective org blocklist when a client's `prcModelsEnabled`
   * is explicitly false. Gateway-side enforcement uses the shared
   * PRC_HOSTED_MODEL_NAMES in @packages/db/queries (which also covers alias
   * forms and catalog names not exposed in this picker catalog).
   */
  export const PRC_HOSTED_CHAT_MODEL_IDS: readonly string[] = (
    CHAT_MODELS as readonly ChatModel[]
  )
    .filter((m) => m.prcHosted)
    .map((m) => m.id);

  /** True when a chat-catalog id is flagged China-hosted (picker ids only — no alias forms). */
  export function isPrcHostedChatModel(modelId: string): boolean {
    return CHAT_MODEL_BY_ID.get(modelId)?.prcHosted === true;
  }

  /**
   * Chat-catalog ids flagged training-gated (AIR-842): serving provider's
   * terms permit training on inputs. Merged into the effective org blocklist
   * unless the client's `trainingModelsEnabled` is explicitly true.
   * Gateway-side enforcement uses TRAINING_GATED_MODEL_NAMES in
   * @packages/db/queries (which also covers alias forms and catalog names
   * not exposed in this picker catalog).
   */
  export const TRAINING_GATED_CHAT_MODEL_IDS: readonly string[] = (
    CHAT_MODELS as readonly ChatModel[]
  )
    .filter((m) => m.trainingGated)
    .map((m) => m.id);

  /**
   * Chat-catalog ids flagged retention-gated (AIR-842): serving provider's
   * retention can't be turned off or verified. Merged into the effective org
   * blocklist unless `unverifiedRetentionModelsEnabled` is explicitly true.
   */
  export const RETENTION_GATED_CHAT_MODEL_IDS: readonly string[] = (
    CHAT_MODELS as readonly ChatModel[]
  )
    .filter((m) => m.retentionGated)
    .map((m) => m.id);

  /** True when a chat-catalog id is flagged training-gated (picker ids only — no alias forms). */
  export function isTrainingGatedChatModel(modelId: string): boolean {
    return CHAT_MODEL_BY_ID.get(modelId)?.trainingGated === true;
  }

  /** True when a chat-catalog id is flagged retention-gated (picker ids only — no alias forms). */
  export function isRetentionGatedChatModel(modelId: string): boolean {
    return CHAT_MODEL_BY_ID.get(modelId)?.retentionGated === true;
  }

  /** Display names actually present in the catalog — keeps the pricing-label maps exhaustive. */
  type ChatModelMaker = (typeof CHAT_MODELS)[number]["provider"];
  type ChatModelComputeProvider = Extract<
    (typeof CHAT_MODELS)[number],
    { computeProvider: string }
  >["computeProvider"];

  /**
   * Compute-provider display names → `provider_pricing` / `litellm_usage_log`
   * provider labels, for models billed by an aggregator (mirrors the
   * litellm-proxy's MODEL_PROVIDER_OVERRIDES attribution). `satisfies` keeps
   * this exhaustive over the catalog: adding or renaming a compute provider
   * without classifying its pricing label is a type error, not a silently
   * unpriced model. OpenRouter maps to null — those models are priced under
   * their maker's label.
   */
  const PRICING_PROVIDER_BY_COMPUTE_PROVIDER = {
    "Together AI": "together",
    "Fireworks AI": "fireworks",
    // "NVIDIA NIM" left the vocabulary with AIR-810 and returned with
    // AIR-869 (opt-in training-gated legs); label matches the pricing rows.
    "NVIDIA NIM": "nvidia_nim",
    DeepInfra: "deepinfra",
    "Novita AI": "novita",
    Baseten: "baseten",
    "Alibaba Cloud": "alibaba",
    Tensormesh: "tensormesh",
    StreamLake: "streamlake",
    "GMI Cloud": "gmicloud",
    "Sail Research": "sail",
    // "ByteDance" → "bytedance" left the vocabulary 2026-09-17 with the
    // AIR-1368 pull of the Ark chat legs; the satisfies clause re-admits it
    // (label matches the seedance-2.5/seedream-5 pricing rows) when a leg
    // with computeProvider "ByteDance" returns.
    "Amazon Bedrock": "bedrock",
    // Plain "OpenRouter" left the vocabulary again 2026-09-03 with the
    // muse-spark-1.2 delist (18+ attestation gate; 1.3 is withheld behind
    // the same gate); the satisfies clause below re-admits the key
    // automatically when an OpenRouter-only model returns (null: priced
    // under the maker's label).
    // Multi-provider same-weights serving (2026-08-18): the OpenRouter +
    // Bedrock legs of one group name bill per-request from the leg that
    // served, and the pricing/ledger rows for the GROUP name live under the
    // maker's label (meta / mistral) — same null-→-maker fallthrough
    // OpenRouter entries used.
    "OpenRouter + Amazon Bedrock": null,
  } as const satisfies Record<ChatModelComputeProvider, string | null>;

  /**
   * Model-maker display names → `provider_pricing` provider labels for
   * first-party serving. Exhaustive over the catalog like the map above.
   * null = maker has no first-party serving on the gateway (aggregator-only),
   * so a model of theirs without a `computeProvider` is a catalog mistake.
   */
  const PRICING_PROVIDER_BY_MAKER = {
    Google: "gemini",
    OpenAI: "openai",
    Anthropic: "anthropic",
    xAI: "grok",
    Perplexity: "perplexity",
    Meta: "meta",
    Mistral: "mistral",
    DeepSeek: "deepseek",
    Moonshot: "kimi",
    // AIR-810: no first-party NVIDIA serving — the NIM catalog is retired
    // from routing, so every NVIDIA model must carry a computeProvider.
    NVIDIA: null,
    Amazon: null,
    Qwen: null,
    "Z.ai": null,
    MiniMax: null,
    // Fireworks Research ships its own post-trained models (ember-1) on its
    // own serverless, so the maker label has first-party serving.
    "Fireworks AI": "fireworks",
    // Reflection (Beam family) has no first-party serving on the gateway —
    // every Beam leg is aggregator-served and must carry a computeProvider.
    Reflection: null,
  } as const satisfies Record<ChatModelMaker, string | null>;

  /**
   * `provider_pricing` / `litellm_usage_log` provider label a chat model bills
   * under (compute provider first, maker otherwise), or null when unknown.
   * Same label vocabulary as CHEAP_TASK_MODEL_PROVIDER above.
   */
  export function getPricingProvider(modelId: string): string | null {
    const model = CHAT_MODEL_BY_ID.get(modelId);
    if (!model) return null;
    const byCompute = model.computeProvider
      ? ((PRICING_PROVIDER_BY_COMPUTE_PROVIDER as Record<string, string | null>)[
          model.computeProvider
        ] ?? null)
      : null;
    return (
      byCompute ?? (PRICING_PROVIDER_BY_MAKER as Record<string, string | null>)[model.provider] ?? null
    );
  }

  // =============================================================================
  // MODEL CAPABILITIES
  // =============================================================================

  /**
   * How a model accepts reasoning/thinking depth control.
   * - `reasoning_effort`: send OpenAI-style `reasoning_effort: "high"`; LiteLLM maps to provider-native shape
   *   (Anthropic 4.6+/4.7 adaptive thinking, OpenAI GPT-5 line, etc.). Do NOT send `thinking.budget_tokens`.
   * - `budget_tokens`: send Anthropic-native `thinking: { type: "enabled", budget_tokens: N }`.
   * - `null`: model does not support a thinking knob; toggle is a no-op.
   */
  export type ThinkingInterface = "reasoning_effort" | "budget_tokens" | null;

  export interface ModelCapabilities {
    thinkingInterface: ThinkingInterface;
    supportsFiles: boolean;
    /** Video input (inline bytes via the gateway). Gemini-routed models only; absent means false. */
    supportsVideo?: boolean;
  }

  /**
   * Per-model capability flags. Non-obvious rules:
   * - Opus 4.7 rejects manual `thinking: {type:"enabled"}` with 400. Adaptive is the only
   *   supported mode; LiteLLM auto-maps `reasoning_effort` → `thinking:{type:"adaptive"}`.
   * - Opus 4.6 / Sonnet 4.6 accept both `budget_tokens` (deprecated) and `reasoning_effort`;
   *   we send `reasoning_effort` to stay on the future-facing interface.
   * - Haiku 4.5 has no documented adaptive support — `budget_tokens` is the only path.
   * - GPT-5.5 takes `reasoning_effort` natively; LiteLLM routes to Responses API when
   *   `reasoning_effort` and tools are both present.
   */
  export const MODEL_CAPABILITIES = {
    // Anthropic
    // Fable 5: thinking is always on (adaptive). reasoning_effort maps to it
    // proxy-side; manual thinking config 400s. temp/top_p/top_k dropped proxy-side.
    "claude-fable-5-1": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-fable-5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // Opus 5 / Sonnet 5 / Sonnet 5.5: adaptive thinking is on by default; manual
    // budget_tokens 400s. Non-default temp/top_p/top_k 400 — dropped proxy-side.
    // Opus 5.5 (2026-10-05): same adaptive-thinking / proxy-side param-strip
    // stance as Opus 5.
    "claude-opus-5-5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-opus-5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-sonnet-5-5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-sonnet-5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-opus-4-8": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-opus-4-7": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-opus-4-6": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "claude-sonnet-4-6": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // Fast-output serving tiers of the same weights — same adaptive-thinking
    // (4.7+) semantics as their base models; temp/top_p/top_k dropped proxy-side.
    // Sonnet 4.5 / Opus 4.5 predate adaptive thinking — budget_tokens is the only path.
    "claude-sonnet-4-5": { thinkingInterface: "budget_tokens", supportsFiles: true },
    "claude-opus-4-5": { thinkingInterface: "budget_tokens", supportsFiles: true },
    "claude-haiku-4-5-20251001": { thinkingInterface: "budget_tokens", supportsFiles: true },

    // OpenAI
    // GPT-5.6 line (pro tiers included) takes reasoning_effort natively (like
    // GPT-5.5); the codex trio is the same reasoning interface. gpt-5.4-nano
    // matches the gpt-5.4 / gpt-5.4-mini family: no thinking knob.
    "gpt-6-astra": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // Ultrafast twins: reasoning_effort stays independent of the service-tier
    // mode pin (the engine entry carries service_tier; effort rides verbatim).
    "gpt-6-astra-ultrafast": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // GPT-6 Sol and 6.1 Sol (AIR-1494): reasoning_effort native, image + file input.
    "gpt-6.1-sol": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-6.1-sol-ultrafast": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-6-sol": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // GPT-6 Luna (2026-10-05): reasoning_effort natively; vision + PDF input
    // (text+image+file->text), same shape as gpt-6-astra.
    "gpt-6-luna": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.6-sol": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // Ultra: reasoning_effort stays independent of the multi-agent mode pin
    // (the engine entry carries multi_agent.enabled; effort rides verbatim).
    "gpt-5.6-sol-ultra": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.6-terra": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.6-luna": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.5": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.5-pro": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    // AIR-1400: gpt-5.x reasoning line takes reasoning_effort natively.
    "gpt-5.4-pro": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.2": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.2-pro": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.1": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.3-codex": { thinkingInterface: "reasoning_effort", supportsFiles: true },
    "gpt-5.4": { thinkingInterface: null, supportsFiles: true },
    "gpt-5.4-mini": { thinkingInterface: null, supportsFiles: true },
    "gpt-5.4-nano": { thinkingInterface: null, supportsFiles: true },
    "gpt-4o": { thinkingInterface: null, supportsFiles: true },
    "gpt-4o-mini": { thinkingInterface: null, supportsFiles: false },
    // GPT-4.1 pair: no reasoning knob; text+image+file input (AIR-1400).
    "gpt-4.1": { thinkingInterface: null, supportsFiles: true },
    "gpt-4.1-mini": { thinkingInterface: null, supportsFiles: true },

    // Google — the only family with video input support (gated gateway-side).
    // Gemini 3.x thinking is managed upstream (thinking tokens included in
    // output pricing) — no OpenAI/Anthropic-style client knob.
    "gemini-3.1-pro-preview": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.8-flash": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.7-flash": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.6-flash": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.5-flash": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.5-flash-lite": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3.1-flash-lite": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-2.5-pro": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-2.5-flash": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },
    "gemini-3-flash-preview": { thinkingInterface: null, supportsFiles: true, supportsVideo: true },

    // xAI / Perplexity / Meta / Mistral
    // grok-4-6 / grok-build-0-1 accept image+file input upstream
    // (text+image+file->text per OpenRouter; the engine deployment declares
    // supports_vision) — file-capable like the GPT-5.4/Gemini entries.
    "grok-4-6": { thinkingInterface: null, supportsFiles: true },
    "grok-build-0-1": { thinkingInterface: null, supportsFiles: true },
    "grok-4-20": { thinkingInterface: null, supportsFiles: false },
    "grok-4-20-non-reasoning": { thinkingInterface: null, supportsFiles: false },
    "grok-4-20-multi-agent": { thinkingInterface: null, supportsFiles: false },
    "grok-4-5": { thinkingInterface: null, supportsFiles: false },
    "grok-4-3": { thinkingInterface: null, supportsFiles: false },
    "grok-4-1-fast-reasoning": { thinkingInterface: null, supportsFiles: false },
    "grok-4-1-fast-non-reasoning": { thinkingInterface: null, supportsFiles: false },
    "grok-3": { thinkingInterface: null, supportsFiles: false },
    "grok-3-mini": { thinkingInterface: null, supportsFiles: false },
    "sonar-pro": { thinkingInterface: null, supportsFiles: false },
    sonar: { thinkingInterface: null, supportsFiles: false },
    "sonar-reasoning-pro": { thinkingInterface: null, supportsFiles: false },
    "sonar-deep-research": { thinkingInterface: null, supportsFiles: false },
    "llama-4-maverick": { thinkingInterface: null, supportsFiles: false },
    "llama-4-scout": { thinkingInterface: null, supportsFiles: false },
    "mistral-large": { thinkingInterface: null, supportsFiles: false },
    // Reflection Beam 1.0 (2026-10-20): reasoning is managed upstream
    // (Together/OpenRouter accept a `reasoning` param; no OpenAI/Anthropic-
    // style client knob — aggregator family convention). Text-only: no
    // vision/PDF input in the LiteLLM cost-map entry.
    "beam-1.0": { thinkingInterface: null, supportsFiles: false },
    // Mistral La Plateforme (maker API). Medium 3.5 is the only vision/PDF
    // entry; La Plateforme exposes no client-side thinking knob.
    "mistral-medium-3.5": { thinkingInterface: null, supportsFiles: true },
    "codestral-2508": { thinkingInterface: null, supportsFiles: false },
    "ministral-14b": { thinkingInterface: null, supportsFiles: false },
    // Small 4 is multimodal (image input, hybrid reasoning managed upstream);
    // the Ministral 3 pair follows the ministral-14b stance until an image
    // smoke runs (AIR-1400).
    "mistral-small-4": { thinkingInterface: null, supportsFiles: true },
    "ministral-8b": { thinkingInterface: null, supportsFiles: false },
    "ministral-3b": { thinkingInterface: null, supportsFiles: false },

    // Amazon Nova (AWS Bedrock). Nova 2 reasoning is a Bedrock-native param —
    // not exposed as a client-side knob. Micro is text-only; the rest take
    // image/PDF input.
    "nova-2-lite": { thinkingInterface: null, supportsFiles: true },
    "nova-pro": { thinkingInterface: null, supportsFiles: true },
    "nova-lite": { thinkingInterface: null, supportsFiles: true },
    "nova-micro": { thinkingInterface: null, supportsFiles: false },

    // DeepSeek (deepseek-reasoner thinks by default — no client-side knob)
    "deepseek-chat": { thinkingInterface: null, supportsFiles: false },
    "deepseek-reasoner": { thinkingInterface: null, supportsFiles: false },
    // V4.1 supports images and adjustable effort upstream. Keep both controls
    // conservative until the UAT baseline proves their gateway wire shapes.
    "deepseek-flash": { thinkingInterface: null, supportsFiles: false },

    // Together AI / Fireworks AI / DeepInfra (open-weight serving; reasoning
    // models manage thinking upstream — no OpenAI/Anthropic-style knob)
    "qwen-3.7-max": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro": { thinkingInterface: null, supportsFiles: false },
    "minimax-m3": { thinkingInterface: null, supportsFiles: false },
    "muse-glimmer": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-20b": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.7-plus": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash": { thinkingInterface: null, supportsFiles: false },
    // Reasoner twins (2026-08-14): thinking is pinned server-side by the
    // gateway config (mode baked into the name, like deepseek-reasoner) —
    // no client-side knob.
    "deepseek-v4-flash-reasoner": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-reasoner-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4.1-flash": { thinkingInterface: null, supportsFiles: false },
    // 1017 checkpoint on Fireworks: same knob posture as the V4.1 Flash
    // primary; text-only until an image smoke passes on the Fireworks leg.
    "deepseek-v4.1-flash-1017": { thinkingInterface: null, supportsFiles: false },
    // Baseten Fast-tier twin of the 1017 checkpoint: same null-knob stance;
    // engine metadata declares vision but files stay off until an image smoke
    // passes on the Baseten leg (flip-after-smoke, glm-5.3-flash-baseten
    // precedent).
    "deepseek-v4.1-flash-1017-fast": { thinkingInterface: null, supportsFiles: false },
    // Same knob posture as the DeepInfra V4.1 Flash primary; text-only until
    // image acceptance is verified on the Novita leg (flip-after-smoke).
    "deepseek-v4.1-flash-novita": { thinkingInterface: null, supportsFiles: false },
    // DeepSeek V3.2 (2026-09-10): thinking managed upstream — no client-side
    // knob; text-only. (-novita leg left 2026-09-29.)
    "deepseek-v3.2": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-novita": { thinkingInterface: null, supportsFiles: false },
    // Thinking-mode twin (AIR-910): pinned server-side via
    // chat_template_kwargs, mode baked into the name — no client knob.
    "deepseek-v4-flash-reasoner-novita": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.8-max-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.8-max-novita": { thinkingInterface: null, supportsFiles: false },
    "llama-3.3-70b": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-120b": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3-super": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3.5-lightning": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3-nano": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3-ultra": { thinkingInterface: null, supportsFiles: false },

    // Tensormesh (AIR-834) — text-only OpenAI-compatible endpoint; no
    // thinking knob exposed until verified against their serving stack.
    "kimi-k2.6-tensormesh": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-tensormesh": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.5-397b": { thinkingInterface: null, supportsFiles: false },
    "qwen3-coder-480b": { thinkingInterface: null, supportsFiles: false },
    "minimax-m2.5-tensormesh": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-tensormesh": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-120b-tensormesh": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-20b-tensormesh": { thinkingInterface: null, supportsFiles: false },
    // StreamLake serving legs (AIR-1302): thinking managed upstream on every
    // leg — no client-side knob, matching each base model's other legs.
    "deepseek-v3.2-streamlake": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-streamlake": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-streamlake": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-streamlake": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-streamlake": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-streamlake": { thinkingInterface: null, supportsFiles: false },
    "minimax-m2.5-streamlake": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.6-27b": { thinkingInterface: null, supportsFiles: false },
    "gemma-4-31b": { thinkingInterface: null, supportsFiles: false },
    // AIR-826 multi-provider serving legs — capabilities mirror each leg's
    // primary (same weights; thinking managed upstream, no client knob).
    "glm-5.2-together": { thinkingInterface: null, supportsFiles: false },
    // AIR-910 Together V4-Flash legs: thinking pinned server-side (chat
    // "none" / reasoner "high"), mode baked into the name — no client knob.
    "deepseek-v4-flash-together": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-reasoner-together": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3-ultra-nvidia": { thinkingInterface: null, supportsFiles: false },
    // K3 is natively multimodal (text+image); the Fireworks serving mirrors
    // the primary's file support — verify at UAT smoke.
    "kimi-k3-fireworks": { thinkingInterface: null, supportsFiles: true },
    // Reasoning always on upstream like K3 (the shorter traces ARE the
    // product) — no client knob; no image pricing published, so files stay
    // off until vision is verified on UAT.
    "ember-1": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "minimax-m3-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-20b-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-120b-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "llama-4-maverick-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "llama-4-scout-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "nemotron-3-nano-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-novita": { thinkingInterface: null, supportsFiles: false },
    // Natively multimodal (text+image in), but ships FALSE until the Novita
    // leg's image acceptance is actually verified at UAT smoke (review on
    // #966; the comment previously claimed a verification that hadn't
    // happened — kimi-k3-fireworks precedent is flip-after-smoke).
    "glm-5.3-flash-novita": { thinkingInterface: null, supportsFiles: false },
    // Natively vision-capable (text+image in per the catalog + Novita model
    // card), but ships FALSE until the Novita leg's image acceptance is
    // verified at UAT smoke (glm-5.3-flash-novita precedent:
    // flip-after-smoke). Thinking is hybrid, managed upstream — no client
    // knob.
    "deepseek-v4-flash-vision-exp-novita": { thinkingInterface: null, supportsFiles: false },
    // Same-weights vision-exp twins (2026-09-07): identical stance to the
    // Novita leg — supportsFiles stays FALSE until each leg's image
    // acceptance is verified at UAT smoke (flip-after-smoke precedent);
    // thinking is hybrid, managed upstream — no client knob.
    "deepseek-v4-flash-vision-exp-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-novita": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-novita": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-novita": { thinkingInterface: null, supportsFiles: false },
    "minimax-m3-novita": { thinkingInterface: null, supportsFiles: false },
    "gpt-oss-20b-novita": { thinkingInterface: null, supportsFiles: false },
    "llama-4-maverick-novita": { thinkingInterface: null, supportsFiles: false },
    "llama-4-scout-novita": { thinkingInterface: null, supportsFiles: false },
    // Baseten serving legs (AIR-839) — same capability surface as their
    // DeepInfra/Novita twins.
    "gpt-oss-120b-baseten": { thinkingInterface: null, supportsFiles: false },
    // DeepSeek V4 Flash legs: thinking is pinned server-side by the gateway
    // config (chat pins reasoning_effort "none", reasoner pins "high") —
    // mode baked into the name, no client-side knob.
    "deepseek-v4-flash-baseten": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-reasoner-baseten": { thinkingInterface: null, supportsFiles: false },
    // Natively multimodal (text+image in per the Baseten library page), but
    // ships FALSE until the leg's image acceptance is verified at UAT smoke
    // (flip-after-smoke precedent, glm-5.3-flash-novita).
    "glm-5.3-flash-baseten": { thinkingInterface: null, supportsFiles: false },
    // AIR-1300 Baseten catalog completion (2026-09-11): capability surface
    // matches the same-weights siblings — no client-side thinking knob,
    // files stay FALSE pre-smoke.
    "glm-5.3-baseten": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-baseten": { thinkingInterface: null, supportsFiles: false },
    // GLM-4.7 serving legs (2026-09-11): thinking managed upstream — no
    // client-side knob; text-only model, so supportsFiles stays FALSE.
    "glm-4.7-novita": { thinkingInterface: null, supportsFiles: false },
    "glm-4.7-deepinfra": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-baseten": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-baseten": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-baseten": { thinkingInterface: null, supportsFiles: false },
    // DeepSeek V4.1 Flash Fast (2026-10-03 registry sync): Baseten-only
    // speed tier. Provider-managed reasoning (the V4.1 family stance) — no
    // client-side knob; upstream card declares vision but the leg's image
    // acceptance is unverified, so supportsFiles stays FALSE until a UAT
    // smoke (glm-5.3-flash-baseten precedent).
    "deepseek-v4.1-flash-fast": { thinkingInterface: null, supportsFiles: false },
    // Beam 1.0 Baseten leg (2026-10-26): same null-knob / text-only stance as
    // the Together primary (reasoning managed upstream).
    "beam-1.0-baseten": { thinkingInterface: null, supportsFiles: false },
    // GMI Cloud serving legs (AIR-1301) — same-weights twins of existing
    // groups; thinking managed upstream where the base has one (serving
    // default unprobed on our key — no client knob until UAT smoke),
    // text-only until image acceptance is verified (flip-after-smoke
    // precedent, glm-5.3-flash-novita).
    "deepseek-v4-flash-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3-flash-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-gmicloud": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-gmicloud": { thinkingInterface: null, supportsFiles: false },
    // Sail Research serving legs (2026-10-30): thinking managed upstream
    // where the base has one (GLM-5.3 on Sail rejects reasoning_effort
    // "none" with a 400, so no client knob); Sail's models page declares
    // image input on Kimi K3 and the Gemma 4 builds but acceptance is
    // unverified on our key — text-only until a UAT smoke proves it
    // (flip-after-smoke precedent, glm-5.3-flash-novita).
    "kimi-k3-sail": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3-sail": { thinkingInterface: null, supportsFiles: false },
    "glm-5.3-flash-sail": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4.1-flash-1017-sail": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-sail": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-flash-sail": { thinkingInterface: null, supportsFiles: false },
    "gemma-4-31b-sail": { thinkingInterface: null, supportsFiles: false },
    "gemma-4-31b-nvfp4": { thinkingInterface: null, supportsFiles: false },
    "gemma-4-12b": { thinkingInterface: null, supportsFiles: false },

    // Moonshot (Kimi)
    // K3 reasoning is always on upstream; at launch only reasoning_effort="max"
    // is accepted, and our reasoning_effort interface sends "high" (rejected) —
    // so no client-side thinking knob.
    "kimi-k3": { thinkingInterface: null, supportsFiles: true },
    // AIR-826 maker-API legs of the US-hosted K2.x names.
    "kimi-k2.7-code-moonshot": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.6-moonshot": { thinkingInterface: null, supportsFiles: false },
    // K2.5 / K2 Thinking: reasoning always on upstream (like K3) — no
    // client-side knob. K2.5 is the vision-capable entry.
    "kimi-k2.5": { thinkingInterface: null, supportsFiles: true },
    "kimi-k2-thinking": { thinkingInterface: null, supportsFiles: false },

    // DeepSeek official API maker leg (AIR-826).
    "deepseek-v4-pro-deepseek": { thinkingInterface: null, supportsFiles: false },

    // Alibaba Cloud Model Studio (official Qwen API). qwen3.8-max is a hybrid
    // reasoner managed upstream (DashScope enable_thinking) — no client-side
    // knob wired yet (follow-up); vision support unverified at launch, so no
    // file routing. Same rules for the rest of the DashScope lineup below
    // (qwen-3-vl-235b-a22b is the one verified vision entry); AIR-826 legs
    // follow the same conservative posture.
    "qwen-3.8-max": { thinkingInterface: null, supportsFiles: false },
    // Qwen3.8 Flash: hybrid reasoner managed upstream (no client-side knob).
    // Keep file routing off until the gateway route is verified for PDF/image
    // attachments and the engine entry declares the corresponding capability.
    "qwen-3.8-flash": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.7-flash": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.6-plus": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.6-flash": { thinkingInterface: null, supportsFiles: false },
    "qwen-3-coder-plus": { thinkingInterface: null, supportsFiles: false },
    "qwen-3-coder-next": { thinkingInterface: null, supportsFiles: false },
    "qwen-3-vl-235b-a22b": { thinkingInterface: null, supportsFiles: true },
    "qwen-3.7-max-alibaba": { thinkingInterface: null, supportsFiles: false },
    "qwen-3.7-plus-alibaba": { thinkingInterface: null, supportsFiles: false },
    "glm-5.2-alibaba": { thinkingInterface: null, supportsFiles: false },
    "deepseek-v4-pro-alibaba": { thinkingInterface: null, supportsFiles: false },
    "kimi-k2.7-code-alibaba": { thinkingInterface: null, supportsFiles: false },
  } as const satisfies Record<string, ModelCapabilities>;

  /** Union of every chat-model id in CHAT_MODELS. */
  export type ChatModelId = keyof typeof MODEL_CAPABILITIES;

  const UNKNOWN_MODEL_CAPABILITIES: ModelCapabilities = {
    thinkingInterface: null,
    supportsFiles: false,
    supportsVideo: false,
  };

  export function getModelCapabilities(id: string): ModelCapabilities {
    return (MODEL_CAPABILITIES as Record<string, ModelCapabilities>)[id] ?? UNKNOWN_MODEL_CAPABILITIES;
  }

  // =============================================================================
  // MODEL VALIDATION & DEFAULT RESOLUTION
  // =============================================================================

  /**
   * Type guard that returns true only for model IDs currently in CHAT_MODELS.
   * Used to reject stale IDs that may linger in stored preferences.
   */
  export function isValidChatModelId(id: string | null | undefined): id is string {
    if (!id) return false;
    return CHAT_MODELS.some((m) => m.id === id);
  }

  /**
   * True for any id a user/org may select as a default: a concrete `CHAT_MODELS`
   * id or the `"auto"` sentinel (Auto Routing). `isValidChatModelId` intentionally
   * excludes `"auto"` (it is not a real model), so anything that stores/accepts the
   * Auto option must use this guard instead.
   */
  export function isSelectableModelId(id: string | null | undefined): id is string {
    return id === AUTO_MODEL_ID || isValidChatModelId(id);
  }

  export type DefaultModelSource = "user" | "org" | "platform";

  export interface ResolvedDefaultModel {
    modelId: string;
    source: DefaultModelSource;
  }

  /**
   * Resolve the effective chat model by walking user → org → platform.
   * Stored IDs are validated so stale values fall through to the next tier.
   * `"auto"` is a valid selection at any tier, and is the platform fallback
   * when neither the user nor the org has set a default (so orgs default to
   * Auto Routing unless an admin picks a specific model).
   *
   * When the org has `modelSelectionLocked`, org policy wins outright and the
   * user's saved preference is ignored (falling back to Auto if the org has no
   * explicit default).
   */
  export function resolveDefaultModelId({
    userPref,
    orgPref,
    locked = false,
  }: {
    userPref?: string | null;
    orgPref?: string | null;
    locked?: boolean;
  }): ResolvedDefaultModel {
    if (locked) {
      if (isSelectableModelId(orgPref)) {
        return { modelId: orgPref, source: "org" };
      }
      return { modelId: AUTO_MODEL_ID, source: "org" };
    }

    if (isSelectableModelId(userPref)) {
      return { modelId: userPref, source: "user" };
    }

    if (isSelectableModelId(orgPref)) {
      return { modelId: orgPref, source: "org" };
    }

    return { modelId: AUTO_MODEL_ID, source: "platform" };
  }

  // =============================================================================
  // LEGACY MODEL ID MIGRATION
  // =============================================================================

  /**
   * Deprecated picker ids → current explicit ids. Read-path shim for
   * `preferences.defaultModelId` so stored values keep working without a backfill.
   * Drop once `migrateDeprecatedDefaultModel` has drained stale values.
   */
  const DEFAULT_MODEL_ID_MIGRATIONS: Record<string, ChatModelId> = {
    "claude-opus": "claude-opus-4-7",
    "claude-sonnet": "claude-sonnet-4-6",
    // Retired by Google 2026-08-31 ("no longer available to new users");
    // successor per Google's own 404 message.
    "gemini-2.5-flash-lite": "gemini-3.5-flash-lite",
    // The AIR-1145 triage removals (2026-09-01) get NO migration rows: the
    // prod ledger shows those entries never served (never-servable
    // misconfigs/entitlement gaps) and mistral-small's creative fine-tune
    // has no same-variant successor. Stored defaults fall back to Auto via
    // the isSelectableModelId guard in resolveDefaultModelId; without a
    // migration row isDeprecatedModelId stays false, so no write-back drains
    // the stale preference — it just resolves to Auto on every read.
    //
    // Together serving withdrawal 2026-09-01 (AIR-1187): same-weights
    // serving re-routes, mirroring the gateway RETIRED_MODEL_ALIASES rows
    // (a providers.test.ts pin asserts the two tables never drift). The
    // Kimi targets are PRC/training-gated and the MiniMax target is
    // retention-gated — the picker's consent gating applies to the migrated
    // value like any other pick. minimax-m2.7 has NO row on purpose: no
    // surviving leg anywhere (RETIRED_MODEL_IDS; falls back to Auto).
    "kimi-k2.7-code": "kimi-k2.7-code-moonshot",
    "kimi-k2.6-together": "kimi-k2.6-moonshot",
    "minimax-m2.5": "minimax-m2.5-tensormesh",
    "nemotron-3-ultra-together": "nemotron-3-ultra",
    // Fireworks serverless retirement 2026-09-25: the DeepInfra legs serve the
    // identical checkpoints (gateway RETIRED_MODEL_ALIASES rows).
    "deepseek-v4-pro-fireworks": "deepseek-v4-pro-deepinfra",
    "deepseek-v4-flash-vision-exp-fireworks": "deepseek-v4-flash-vision-exp-deepinfra",
    // 2026-09-29: Novita and GMI Cloud stopped serving DeepSeek V3.2; the
    // DeepInfra primary serves the identical weights (gateway
    // RETIRED_MODEL_ALIASES rows). Zero lifetime usage on either leg.
    "deepseek-v3.2-novita": "deepseek-v3.2",
    "deepseek-v3.2-gmicloud": "deepseek-v3.2",
    // 2026-10-05: Baseten retired glm-4.7-baseten (HTTP 410); the Novita
    // primary serves the identical weights at the same card (gateway
    // RETIRED_MODEL_ALIASES row, AIR-1187 flavor). No consent gate on the
    // target.
    "glm-4.7-baseten": "glm-4.7-novita",
  };

  export function normalizeDefaultModelId(id: string | null | undefined): string {
    if (!id) return DEFAULT_CHAT_MODEL_ID;
    return DEFAULT_MODEL_ID_MIGRATIONS[id] ?? id;
  }

  export function isDeprecatedModelId(id: string | null | undefined): boolean {
    return !!id && id in DEFAULT_MODEL_ID_MIGRATIONS;
  }

  // =============================================================================
  // EXTENDED THINKING REQUEST PATCHING
  // =============================================================================

  /**
   * Default thinking budget (in tokens) when extended thinking is enabled via the
   * `budget_tokens` interface (Haiku 4.5).
   */
  const EXTENDED_THINKING_BUDGET_TOKENS = 50_000;

  /**
   * Anthropic pre-4.7 models 400 if `temperature` is anything other than 1 while
   * extended thinking is on. Strip it client-side. Opus 4.7 handles the same
   * constraint at the proxy via `additional_drop_params`.
   */
  const PRE_47_THINKING_TEMP_STRIP = new Set<ChatModelId>([
    "claude-opus-4-6",
    "claude-opus-4-5",
    "claude-sonnet-4-6",
    "claude-sonnet-4-5",
    "claude-haiku-4-5-20251001",
  ]);

  /**
   * True when `modelId` rejects a `temperature` while extended thinking is on
   * (the PRE_47_THINKING_TEMP_STRIP set). Exported for request builders that
   * translate a chosen effort themselves (the Playground) so they strip the
   * same way this module does.
   */
  export function thinkingRejectsTemperature(modelId: string): boolean {
    return PRE_47_THINKING_TEMP_STRIP.has(modelId as ChatModelId);
  }

  /** Thinking budget per effort for `budget_tokens` models; "high" matches applyExtendedThinking's default budget. */
  export const THINKING_BUDGET_TOKENS_BY_EFFORT = {
    low: 4_000,
    medium: 16_000,
    high: EXTENDED_THINKING_BUDGET_TOKENS,
    xhigh: EXTENDED_THINKING_BUDGET_TOKENS,
  } as const;

  export function applyExtendedThinking(body: Record<string, unknown>, modelId: string): void {
    const iface = getModelCapabilities(modelId).thinkingInterface;
    if (iface === "reasoning_effort") {
      body.reasoning_effort = "high";
      delete body.thinking;
    } else if (iface === "budget_tokens") {
      body.thinking = {
        type: "enabled",
        budget_tokens: EXTENDED_THINKING_BUDGET_TOKENS,
      };
    }
    if (PRE_47_THINKING_TEMP_STRIP.has(modelId as ChatModelId)) {
      delete body.temperature;
    }
  }

  function createThinkingFetch(
    extraHeaders: Record<string, string>,
    modelId: string,
    onServedModel?: OnServedModel,
  ): typeof globalThis.fetch {
    return (async (input, init) => {
      const headers = await createLitellmHeaders(init?.headers);

      for (const [key, value] of Object.entries(extraHeaders)) {
        headers.set(key, value);
      }

      if (init?.body && typeof init.body === "string") {
        try {
          const body = JSON.parse(init.body) as Record<string, unknown>;
          applyExtendedThinking(body, modelId);
          return reportServedModel(
            await fetch(input, { ...init, headers, body: JSON.stringify(body) }),
            onServedModel,
          );
        } catch {
          // body wasn't JSON
        }
      }

      return reportServedModel(await fetch(input, { ...init, headers }), onServedModel);
    }) as typeof globalThis.fetch;
  }

  /**
   * Create a model instance from a LiteLLM model name.
   * Returns an AI SDK LanguageModel routed through the LiteLLM proxy.
   */
  export function createModelConfig(
    litellmModelName: string,
    extraHeaders: Record<string, string> = {},
    options?: { extendedThinking?: boolean; onServedModel?: OnServedModel },
  ) {
    const useThinking =
      options?.extendedThinking === true &&
      getModelCapabilities(litellmModelName).thinkingInterface !== null;
    const onServedModel = options?.onServedModel;

    if (useThinking) {
      // Extended thinking requires a custom fetch wrapper to inject thinking params
      const provider = createLitellmProvider(
        createThinkingFetch(extraHeaders, litellmModelName, onServedModel),
      );
      return provider.chatModel(litellmModelName);
    }

    if (Object.keys(extraHeaders).length === 0 && !onServedModel) {
      return litellm.chatModel(litellmModelName);
    }

    return createScopedLitellm(extraHeaders, onServedModel).chatModel(litellmModelName);
  }

  // =============================================================================
  // MODEL ACCESS FUNCTIONS
  // =============================================================================

  /**
   * Get model metadata by ID.
   *
   * @param modelId - The model ID to look up
   * @returns The model metadata or undefined
   */
  export function getModelMetadata(modelId: string): ModelMetadata | undefined {
    return MODEL_CATALOG.find((m) => m.id === modelId);
  }

  // =============================================================================
  // DEFAULT CONFIGURATION
  // =============================================================================

